import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { DraftZone } from '../model/builder.store.js';
import { rectWithinFloor, type WarehouseFloor } from '../model/plan-geometry.js';

export interface ZoneFormValues {
  code: string;
  name: string;
  color: string;
  x: number;
  y: number;
  w: number;
  d: number;
}

export interface ZoneEditorProps {
  /** `null` — yangi zona. */
  zone: DraftZone | null;
  floor: WarehouseFloor;
  onSave: (values: ZoneFormValues) => void;
  onCancel?: () => void;
  /**
   * Ustada `true`: joyni dastur topadi, shuning uchun X/Y maydonlari
   * chizilmaydi. Panelda `false` — u yerda zonani aniq koordinataga
   * surish kerak bo'ladi.
   */
  autoPlace?: boolean;
  /** Har o'zgarishda chaqiriladi — usta joyni shu bo'yicha qidiradi. */
  onChange?: (values: ZoneFormValues) => void;
  /** Yangi zonaga taklif qilinadigan kod — navbatdagi bo'sh harf. */
  defaultCode?: string;
  submitLabel?: string;
  className?: string;
}

/**
 * Zona rangi backendda `#RRGGBB` bo'lib saqlanadi — bu MA'LUMOT, dizayn
 * tokeni emas: uni odam tanlaydi va u tema bilan almashmaydi. Shuning uchun
 * bu yerda HEX turishi to'g'ri.
 *
 * Lekin BOSHLANG'ICH qiymat brendga ergashadi: yangi chizilgan zona ilova
 * bilan bir tilda ko'rinishi kerak. Ilgari bu qiymat ikki joyda takrorlangan
 * edi va qayta ranglashdan keyin ulardan biri eski yashil bo'lib qolardi —
 * shuning uchun endi bitta joydan eksport qilinadi.
 */
export const DEFAULT_ZONE_COLOR = '#003d64';
const DEFAULT_COLOR = DEFAULT_ZONE_COLOR;

const valuesOf = (zone: DraftZone | null, defaultCode?: string): ZoneFormValues =>
  zone === null
    ? { code: defaultCode ?? 'A', name: '', color: DEFAULT_COLOR, x: 0, y: 0, w: 10, d: 10 }
    : {
        code: zone.code,
        name: zone.name,
        color: zone.color,
        x: zone.plan.x,
        y: zone.plan.y,
        w: zone.plan.w,
        d: zone.plan.d,
      };

const toNumber = (raw: string): number => {
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) ? value : 0;
};

interface ZoneErrors {
  code?: string;
  name?: string;
  rect?: string;
}

const validate = (values: ZoneFormValues, floor: WarehouseFloor): ZoneErrors => {
  const errors: ZoneErrors = {};
  // Kod naqshi shartnomadagi `zoneCodeSchema` bilan bir xil bo'lishi kerak.
  if (!/^[A-Z][A-Z0-9]{0,2}$/.test(values.code)) errors.code = t('builder.errorZoneCode');
  if (values.name.trim().length < 2) errors.name = t('builder.errorZoneName');
  if (!rectWithinFloor({ x: values.x, y: values.y, w: values.w, d: values.d }, floor)) {
    errors.rect = t('builder.errorZoneRect');
  }
  return errors;
};

/**
 * Zona xossalari.
 *
 * Kod faqat YANGI zonada tahrirlanadi. Sabab shartnomada: `updateZoneSchema`
 * da `code` umuman yo'q — zona kodi katak kodining bir qismi (`A-03-02-05`),
 * katak kodi esa javonga yopishtirilgan etiketkada turadi va o'zgarmaydi.
 * Maydonni ochiq qoldirsak, kiritilgan kod jimgina yo'qolib ketardi.
 */
export const ZoneEditor = ({
  zone,
  floor,
  onSave,
  onCancel,
  autoPlace = false,
  onChange,
  defaultCode,
  submitLabel,
  className,
}: ZoneEditorProps) => {
  const [values, setValues] = useState<ZoneFormValues>(() => valuesOf(zone, defaultCode));
  const [touched, setTouched] = useState(false);
  const colorId = useId();

  /* Tashqi kuzatuvchi (usta) qiymat o'zgarganda xabardor bo'ladi.
     `useEffect` da: holatni yangilash paytida boshqa komponentni yangilash
     React da taqiqlangan. */
  useEffect(() => {
    onChange?.(values);
  }, [values, onChange]);

  const errors = useMemo(
    // Ustada joyni dastur topadi — X/Y ni odam kiritmaydi, demak
    // "chegaradan chiqdi" xatosini unga ko'rsatishning ma'nosi yo'q.
    () => (autoPlace ? validate({ ...values, x: 0, y: 0 }, floor) : validate(values, floor)),
    [values, floor, autoPlace],
  );
  const invalid = Object.keys(errors).length > 0;

  const set = <K extends keyof ZoneFormValues>(key: K, value: ZoneFormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const show = (message: string | undefined): string | undefined => (touched ? message : undefined);

  return (
    <form
      noValidate
      className={cn('flex flex-col gap-3', className)}
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (!invalid) onSave({ ...values, code: values.code.toUpperCase() });
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Input
          mono
          label={t('builder.fieldZoneCode')}
          value={values.code}
          error={show(errors.code)}
          readOnly={zone !== null}
          hint={zone === null ? undefined : t('builder.zoneCodeLocked')}
          onChange={(event) => set('code', event.target.value.toUpperCase())}
        />
        <Input
          label={t('builder.fieldZoneName')}
          value={values.name}
          error={show(errors.name)}
          onChange={(event) => set('name', event.target.value)}
        />
        {autoPlace ? null : (
          <>
            <Input
              mono
              type="number"
              step={0.25}
              label={t('builder.fieldX')}
              value={String(values.x)}
              error={show(errors.rect)}
              onChange={(event) => set('x', toNumber(event.target.value))}
            />
            <Input
              mono
              type="number"
              step={0.25}
              label={t('builder.fieldY')}
              value={String(values.y)}
              onChange={(event) => set('y', toNumber(event.target.value))}
            />
          </>
        )}
        <Input
          mono
          type="number"
          step={0.25}
          label={t('builder.fieldW')}
          value={String(values.w)}
          onChange={(event) => set('w', toNumber(event.target.value))}
        />
        <Input
          mono
          type="number"
          step={0.25}
          label={t('builder.fieldD')}
          value={String(values.d)}
          onChange={(event) => set('d', toNumber(event.target.value))}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor={colorId} className="text-sm font-medium text-text">
            {t('builder.fieldZoneColor')}
          </label>
          <input
            id={colorId}
            type="color"
            value={values.color}
            onChange={(event) => set('color', event.target.value)}
            className={cn(
              'h-[var(--size-control)] w-full rounded-[var(--radius-sm)]',
              'border border-border-control bg-surface px-1',
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          {submitLabel ?? t('builder.save')}
        </Button>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('builder.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
};
